import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import BillingNav from './BillingNav';
import { BillingPage } from './billingUi';
import { downloadInvoicesCsv, fetchBillingReport, formatMoney } from '../../services/billingApi';
import { fetchTenants } from '../../services/licensingApi';

const KINDS = [
  { id: 'summary', label: 'Billing summary' },
  { id: 'tenant-history', label: 'Tenant billing history' },
  { id: 'usage', label: 'Usage & overage' },
  { id: 'receivables', label: 'Accounts receivable' },
  { id: 'audit', label: 'Audit report' },
];

const cell = (v) => (v == null || v === '' ? '—' : String(v).replace('T', ' ').slice(0, 19));

const BillingReports = () => {
  const [searchParams] = useSearchParams();
  const [kind, setKind] = useState(searchParams.get('kind') || 'summary');
  const [tenantId, setTenantId] = useState(searchParams.get('tenantId') || '');
  const [tenants, setTenants] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchTenants().then(setTenants).catch(() => setTenants([]));
  }, []);

  const load = useCallback(async () => {
    if (kind === 'tenant-history' && !tenantId) {
      setData(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      setData(await fetchBillingReport(kind, kind === 'tenant-history' ? tenantId : undefined));
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [kind, tenantId]);

  useEffect(() => {
    load();
  }, [load]);

  const periods = data?.periods || [];
  const rows = Array.isArray(data) ? data : [];

  return (
    <BillingPage>
      <BillingNav title="Reports" />
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <FormControl size="small" sx={{ minWidth: 240 }}>
          <InputLabel>Report</InputLabel>
          <Select label="Report" value={kind} onChange={(e) => setKind(e.target.value)}>
            {KINDS.map((k) => (
              <MenuItem key={k.id} value={k.id}>{k.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        {kind === 'tenant-history' && (
          <FormControl size="small" sx={{ minWidth: 240 }}>
            <InputLabel>Tenant</InputLabel>
            <Select label="Tenant" value={tenantId} onChange={(e) => setTenantId(e.target.value)}>
              {tenants.map((t) => (
                <MenuItem key={t.tenantId} value={String(t.tenantId)}>
                  {t.tenantEmail || t.tenantName || t.tenantId}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        <Button variant="outlined" onClick={() => downloadInvoicesCsv().catch((e) => setError(e.message))}>
          Export invoices CSV
        </Button>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Renewal report stays on the Renewals tab. CSV export is the accounting handoff.
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : kind === 'summary' ? (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Period</TableCell>
              <TableCell align="right">Invoiced</TableCell>
              <TableCell align="right">Payments</TableCell>
              <TableCell align="right">Outstanding</TableCell>
              <TableCell align="right">Overdue</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {periods.length === 0 ? (
              <TableRow><TableCell colSpan={5}>No invoices yet.</TableCell></TableRow>
            ) : periods.map((p) => (
              <TableRow key={p.period}>
                <TableCell>{p.period}</TableCell>
                <TableCell align="right">{formatMoney(p.invoiced)}</TableCell>
                <TableCell align="right">{formatMoney(p.payments)}</TableCell>
                <TableCell align="right">{formatMoney(p.outstanding)}</TableCell>
                <TableCell align="right">{formatMoney(p.overdue)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : kind === 'tenant-history' ? (
        !tenantId ? <Typography color="text.secondary">Select a tenant.</Typography> : (
          <>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>Invoices</Typography>
            <SimpleTable rows={data?.invoices || []} />
            <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>Payments</Typography>
            <SimpleTable rows={data?.payments || []} />
            <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>Credits & adjustments</Typography>
            <SimpleTable rows={data?.adjustments || []} />
          </>
        )
      ) : (
        <SimpleTable rows={rows} />
      )}
    </BillingPage>
  );
};

const SimpleTable = ({ rows }) => {
  const keys = rows[0] ? Object.keys(rows[0]) : [];
  if (!keys.length) return <Typography color="text.secondary">No rows.</Typography>;
  return (
    <Table size="small">
      <TableHead>
        <TableRow>{keys.map((k) => <TableCell key={k}>{k}</TableCell>)}</TableRow>
      </TableHead>
      <TableBody>
        {rows.map((r, i) => (
          <TableRow key={i}>
            {keys.map((k) => <TableCell key={k}>{cell(r[k])}</TableCell>)}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

export default BillingReports;
